import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, concatMap, forkJoin, map, of } from 'rxjs';

import { H5pContentService } from '@services/h5p-content.service';
import { Member } from '@models/member.model';
import { environment } from '@environment';
import {
  CourseOutline,
  BulkCreateResponse,
  buildBulkCreatePayload,
  ChatApiResponse,
  ModuleContentResponse,
  isAiContentSupported,
  isAiImageSupported,
  SuggestedModuleType,
  SkeletonRequestPayload,
  SkeletonResponse,
  SectionContentModule,
  SessionResponse,
  WrappedResponse,
} from '@core/constants/guided-course-creation.constants';

export interface GuidedCourseCreationInput {
  title: string;
  language: string;
  estimatedTimeInMinutes: number;
  outline: CourseOutline;
  author: Member;
  contentGenerationEnabled: boolean;
  sessionId: string;
}

interface CreateFromOutlineActivity {
  h5p_library: string;
  content_id: string;
  content_json: string;
  order: number;
  ai_generated: boolean;
}

interface CreateFromOutlineSection {
  title: string;
  order: number;
  activities: CreateFromOutlineActivity[];
}

interface CreateFromOutlinePayload {
  course: {
    name: string;
    language: string;
    estimated_time_in_minutes: number;
    author_id: number;
    sections: CreateFromOutlineSection[];
  };
}

interface CreateFromOutlineResponse {
  data: { id: string };
}

@Injectable({ providedIn: 'root' })
export class GuidedCourseCreationService {
  private readonly http = inject(HttpClient);
  private readonly h5pContentService = inject(H5pContentService);

  sendChatMessage(
    sessionId: string,
    message: string,
  ): Observable<ChatApiResponse> {
    return this.http
      .post<
        WrappedResponse<ChatApiResponse>
      >(`${environment.backendBaseUrl}/api/v1/guided_course_creation/sessions/${sessionId}/outline/user_message`, { message })
      .pipe(map(response => response.data));
  }

  confirmActions(sessionId: string): Observable<ChatApiResponse> {
    return this.http
      .post<
        WrappedResponse<ChatApiResponse>
      >(`${environment.backendBaseUrl}/api/v1/guided_course_creation/sessions/${sessionId}/outline/confirm_actions`, {})
      .pipe(map(response => response.data));
  }

  cancelActions(sessionId: string): Observable<ChatApiResponse> {
    return this.http
      .post<
        WrappedResponse<ChatApiResponse>
      >(`${environment.backendBaseUrl}/api/v1/guided_course_creation/sessions/${sessionId}/outline/cancel_actions`, {})
      .pipe(map(response => response.data));
  }

  assistantIntroduction(sessionId: string): Observable<ChatApiResponse> {
    return this.http
      .post<
        WrappedResponse<ChatApiResponse>
      >(`${environment.backendBaseUrl}/api/v1/guided_course_creation/sessions/${sessionId}/outline/assistant_introduction`, {})
      .pipe(map(response => response.data));
  }

  restartChat(sessionId: string): Observable<ChatApiResponse> {
    return this.http
      .post<
        WrappedResponse<ChatApiResponse>
      >(`${environment.backendBaseUrl}/api/v1/guided_course_creation/sessions/${sessionId}/outline/restart_chat`, {})
      .pipe(map(response => response.data));
  }

  updateWhatToCover(
    sessionId: string,
    moduleId: number,
    whatToCover: string,
  ): Observable<void> {
    return this.http.post<void>(
      `${environment.backendBaseUrl}/api/v1/guided_course_creation/sessions/${sessionId}/outline/update_what_to_cover`,
      { module_id: moduleId, what_to_cover: whatToCover },
    );
  }

  generateModuleContent(
    sessionId: string,
    moduleId: number,
    imageContent = false,
  ): Observable<ModuleContentResponse> {
    const body = imageContent
      ? { target_module_id: moduleId, image_content: true }
      : { target_module_id: moduleId };
    return this.http
      .post<
        WrappedResponse<ModuleContentResponse>
      >(`${environment.backendBaseUrl}/api/v1/guided_course_creation/sessions/${sessionId}/outline/module_content`, body)
      .pipe(map(response => response.data));
  }

  createSession(): Observable<string> {
    return this.http
      .post<SessionResponse>(
        `${environment.backendBaseUrl}/api/v1/guided_course_creation/sessions`,
        { data: { type: 'sessions' } },
      )
      .pipe(map(response => response.data.id));
  }

  generateSkeleton(
    sessionId: string,
    payload: SkeletonRequestPayload,
  ): Observable<SkeletonResponse> {
    return this.http
      .post<
        WrappedResponse<SkeletonResponse>
      >(`${environment.backendBaseUrl}/api/v1/guided_course_creation/sessions/${sessionId}/outline/skeleton`, payload)
      .pipe(map(response => response.data));
  }

  generateSectionContent(
    sessionId: string,
    sectionId: number,
  ): Observable<SectionContentModule[]> {
    return this.http
      .post<
        WrappedResponse<SectionContentModule[]>
      >(`${environment.backendBaseUrl}/api/v1/guided_course_creation/sessions/${sessionId}/outline/section_content`, { target_section_id: sectionId })
      .pipe(map(response => response.data));
  }

  create(input: GuidedCourseCreationInput): Observable<{ id: string }> {
    if (!input.contentGenerationEnabled) {
      const payload = buildBulkCreatePayload(input.outline, input.language);
      return this.h5pContentService
        .bulkCreate(payload)
        .pipe(
          concatMap(bulkResult =>
            this.callCreateFromOutline(input, bulkResult),
          ),
        );
    }

    return this.generateAllModuleContent(input).pipe(
      map(contentMap =>
        buildBulkCreatePayload(input.outline, input.language, contentMap),
      ),
      concatMap(payload => this.h5pContentService.bulkCreate(payload)),
      concatMap(bulkResult => this.callCreateFromOutline(input, bulkResult)),
    );
  }

  private generateAllModuleContent(
    input: GuidedCourseCreationInput,
  ): Observable<Map<number, ModuleContentResponse>> {
    const requests: {
      moduleId: number;
      rawModuleType?: SuggestedModuleType;
    }[] = [];

    input.outline.sections.forEach(section => {
      section.modules.forEach(mod => {
        if (isAiContentSupported(mod.rawModuleType) && mod.moduleId) {
          requests.push({
            moduleId: mod.moduleId,
            rawModuleType: mod.rawModuleType,
          });
        }
      });
    });

    if (!requests.length) {
      return of(new Map());
    }

    return forkJoin(
      requests.map(req =>
        this.generateModuleContent(
          input.sessionId,
          req.moduleId,
          isAiImageSupported(req.rawModuleType),
        ).pipe(
          map(
            content =>
              [req.moduleId, content] as [number, ModuleContentResponse],
          ),
          catchError(() => of(null as [number, ModuleContentResponse] | null)),
        ),
      ),
    ).pipe(
      map(results => {
        const contentMap = new Map<number, ModuleContentResponse>();
        for (const result of results) {
          if (result) {
            const [moduleId, content] = result;
            contentMap.set(moduleId, content);
          }
        }
        return contentMap;
      }),
    );
  }

  private callCreateFromOutline(
    input: GuidedCourseCreationInput,
    bulkResult: BulkCreateResponse,
  ): Observable<{ id: string }> {
    const payload: CreateFromOutlinePayload = {
      course: {
        name: input.title,
        language: input.language,
        estimated_time_in_minutes: input.estimatedTimeInMinutes,
        author_id: Number(input.author.id),
        sections: input.outline.sections.map((section, sectionIndex) => {
          const bulkSection = bulkResult.sections[sectionIndex];
          return {
            title: section.headline,
            order: sectionIndex,
            activities: section.modules.map((_, moduleIndex) => {
              const bulkModule = bulkSection?.modules[moduleIndex];
              return {
                h5p_library: bulkModule?.h5p_library ?? '',
                content_id: bulkModule?.content_id ?? '',
                content_json: bulkModule?.content_json ?? '{}',
                order: moduleIndex,
                ai_generated: true,
              };
            }),
          };
        }),
      },
    };

    return this.http
      .post<CreateFromOutlineResponse>(
        `${environment.backendBaseUrl}/api/v1/courses/create_from_outline`,
        payload,
        { withCredentials: true },
      )
      .pipe(map(response => ({ id: response.data.id })));
  }
}
